/**
 * Loading State Manager
 * Provides centralized loading state management with Persian language support
 * Handles different types of loading indicators and user feedback
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { PersianText } from '../shared/PersianText';

export interface LoadingState {
  id: string;
  isLoading: boolean;
  message?: string;
  persianMessage?: string;
  progress?: number;
  type: 'spinner' | 'progress' | 'skeleton' | 'overlay' | 'inline';
  context?: string;
  startTime: number;
  timeout?: number;
}

export interface LoadingOptions {
  message?: string;
  persianMessage?: string;
  type?: LoadingState['type'];
  context?: string;
  timeout?: number;
  showProgress?: boolean;
}

interface LoadingStateManagerContextType {
  loadingStates: LoadingState[];
  startLoading: (id: string, options?: LoadingOptions) => void;
  stopLoading: (id: string) => void;
  updateProgress: (id: string, progress: number) => void;
  isLoading: (id?: string) => boolean;
  getLoadingState: (id: string) => LoadingState | undefined;
  clearAllLoading: () => void;
}

const LoadingStateManagerContext = createContext<LoadingStateManagerContextType | null>(null);

interface LoadingStateManagerProps {
  children: React.ReactNode;
  defaultTimeout?: number;
}

export const LoadingStateManager: React.FC<LoadingStateManagerProps> = ({
  children,
  defaultTimeout = 30000 // 30 seconds
}) => {
  const [loadingStates, setLoadingStates] = useState<LoadingState[]>([]);

  const startLoading = useCallback((id: string, options: LoadingOptions = {}) => {
    const loadingState: LoadingState = {
      id,
      isLoading: true,
      message: options.message,
      persianMessage: options.persianMessage,
      type: options.type || 'spinner',
      context: options.context,
      startTime: Date.now(),
      timeout: options.timeout || defaultTimeout,
      progress: options.showProgress ? 0 : undefined
    };

    setLoadingStates(prev => {
      const filtered = prev.filter(state => state.id !== id);
      return [...filtered, loadingState];
    });

    // Auto-stop loading after timeout
    if (loadingState.timeout) {
      setTimeout(() => {
        stopLoading(id);
      }, loadingState.timeout);
    }
  }, [defaultTimeout]);

  const stopLoading = useCallback((id: string) => {
    setLoadingStates(prev => prev.filter(state => state.id !== id));
  }, []);

  const updateProgress = useCallback((id: string, progress: number) => {
    setLoadingStates(prev => prev.map(state => 
      state.id === id ? { ...state, progress: Math.max(0, Math.min(100, progress)) } : state
    ));
  }, []);

  const isLoading = useCallback((id?: string) => {
    if (id) {
      return loadingStates.some(state => state.id === id && state.isLoading);
    }
    return loadingStates.some(state => state.isLoading);
  }, [loadingStates]);

  const getLoadingState = useCallback((id: string) => {
    return loadingStates.find(state => state.id === id);
  }, [loadingStates]);

  const clearAllLoading = useCallback(() => {
    setLoadingStates([]);
  }, []);

  // Cleanup long-running loading states
  useEffect(() => {
    const cleanup = setInterval(() => {
      const now = Date.now();
      setLoadingStates(prev => prev.filter(state => {
        const elapsed = now - state.startTime;
        return elapsed < (state.timeout || defaultTimeout);
      }));
    }, 5000);

    return () => clearInterval(cleanup);
  }, [defaultTimeout]);

  const contextValue: LoadingStateManagerContextType = {
    loadingStates,
    startLoading,
    stopLoading,
    updateProgress,
    isLoading,
    getLoadingState,
    clearAllLoading
  };

  return (
    <LoadingStateManagerContext.Provider value={contextValue}>
      {children}
      <LoadingIndicatorContainer loadingStates={loadingStates} />
    </LoadingStateManagerContext.Provider>
  );
};

/**
 * Loading Indicator Container
 * Renders appropriate loading indicators based on active loading states
 */
interface LoadingIndicatorContainerProps {
  loadingStates: LoadingState[];
}

const LoadingIndicatorContainer: React.FC<LoadingIndicatorContainerProps> = ({ loadingStates }) => {
  const overlayStates = loadingStates.filter(state => state.type === 'overlay');
  
  if (overlayStates.length === 0) return null;

  // Show the most recent overlay loading state
  const currentState = overlayStates[overlayStates.length - 1];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50" dir="rtl">
      <div className="bg-white rounded-lg p-6 max-w-sm mx-4 shadow-xl">
        <div className="flex items-center space-x-4">
          <div className="flex-shrink-0">
            <LoadingSpinner size="lg" />
          </div>
          <div className="flex-1 min-w-0">
            <PersianText 
              text={currentState.persianMessage || currentState.message || 'Loading...'}
              className="text-gray-900 font-medium"
            />
            {currentState.progress !== undefined && (
              <div className="mt-2">
                <div className="bg-gray-200 rounded-full h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${currentState.progress}%` }}
                  />
                </div>
                <PersianText 
                  text={`${Math.round(currentState.progress)}%`}
                  className="text-sm text-gray-600 mt-1"
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Loading Spinner Component
 */
interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  color?: string;
  className?: string;
}

export const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = 'md',
  color = 'text-blue-600',
  className = ''
}) => {
  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-12 h-12'
  };

  return (
    <div className={`animate-spin ${sizeClasses[size]} ${color} ${className}`}>
      <svg fill="none" viewBox="0 0 24 24">
        <circle
          className="opacity-25"
          cx="12"
          cy="12"
          r="10"
          stroke="currentColor"
          strokeWidth="4"
        />
        <path
          className="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </div>
  );
};

/**
 * Inline Loading Component
 */
interface InlineLoadingProps {
  message?: string;
  persianMessage?: string;
  size?: 'sm' | 'md' | 'lg';
  showSpinner?: boolean;
  className?: string;
}

export const InlineLoading: React.FC<InlineLoadingProps> = ({
  message,
  persianMessage,
  size = 'md',
  showSpinner = true,
  className = ''
}) => {
  return (
    <div className={`flex items-center space-x-3 ${className}`} dir="rtl">
      {showSpinner && <LoadingSpinner size={size} />}
      <PersianText 
        text={persianMessage || message || 'Loading...'}
        className="text-gray-600"
      />
    </div>
  );
};

/**
 * Progress Bar Component
 */
interface ProgressBarProps {
  progress: number;
  message?: string;
  persianMessage?: string;
  showPercentage?: boolean;
  className?: string;
}

export const ProgressBar: React.FC<ProgressBarProps> = ({
  progress,
  message,
  persianMessage,
  showPercentage = true,
  className = ''
}) => {
  const clampedProgress = Math.max(0, Math.min(100, progress));

  return (
    <div className={`w-full ${className}`} dir="rtl">
      {(message || persianMessage) && (
        <div className="flex justify-between items-center mb-2">
          <PersianText 
            text={persianMessage || message || ''}
            className="text-sm text-gray-700"
          />
          {showPercentage && (
            <span className="text-sm text-gray-600">{Math.round(clampedProgress)}%</span>
          )}
        </div>
      )}
      <div className="bg-gray-200 rounded-full h-2">
        <div 
          className="bg-blue-600 h-2 rounded-full transition-all duration-300 ease-out"
          style={{ width: `${clampedProgress}%` }}
        />
      </div>
    </div>
  );
};

/**
 * Skeleton Loading Component
 */
interface SkeletonProps {
  lines?: number;
  height?: string;
  className?: string;
  animate?: boolean;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  lines = 3,
  height = 'h-4',
  className = '',
  animate = true
}) => {
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`bg-gray-200 rounded ${height} ${animate ? 'animate-pulse' : ''}`}
          style={{
            width: index === lines - 1 ? '75%' : '100%'
          }}
        />
      ))}
    </div>
  );
};

/**
 * Hook to use loading state manager
 */
export const useLoadingState = () => {
  const context = useContext(LoadingStateManagerContext);
  if (!context) {
    throw new Error('useLoadingState must be used within a LoadingStateManager');
  }
  return context;
};

/**
 * Hook for async operations with loading state
 */
export const useAsyncOperation = () => {
  const { startLoading, stopLoading, updateProgress } = useLoadingState();

  const executeWithLoading = useCallback(async <T,>(
    operation: (updateProgress?: (progress: number) => void) => Promise<T>,
    options: LoadingOptions & { id: string }
  ): Promise<T> => {
    const { id, ...loadingOptions } = options;
    
    try {
      startLoading(id, loadingOptions);
      
      const progressCallback = loadingOptions.showProgress 
        ? (progress: number) => updateProgress(id, progress)
        : undefined;
      
      const result = await operation(progressCallback);
      return result;
    } finally {
      stopLoading(id);
    }
  }, [startLoading, stopLoading, updateProgress]);

  return { executeWithLoading };
};

export default LoadingStateManager;